import React from 'react';
import { motion } from 'framer-motion';
import { UserIndustry } from '../contexts/UserPreferencesContext';

interface IndustrySelectorProps {
  selectedIndustry: UserIndustry;
  onSelect: (industry: UserIndustry) => void;
}

interface IndustryOption {
  id: UserIndustry;
  title: string;
  icon: string;
  color: string;
  selectedColor: string;
}

const industryOptions: IndustryOption[] = [
  {
    id: 'healthcare',
    title: 'Healthcare',
    icon: '🏥',
    color: 'hover:border-teal-400/50',
    selectedColor: 'border-teal-500 bg-gradient-to-br from-teal-500/20 to-emerald-500/20',
  },
  {
    id: 'fintech',
    title: 'FinTech',
    icon: '💳',
    color: 'hover:border-amber-400/50',
    selectedColor: 'border-amber-500 bg-gradient-to-br from-amber-500/20 to-orange-500/20',
  },
  {
    id: 'technology',
    title: 'Technology',
    icon: '🚀',
    color: 'hover:border-indigo-400/50',
    selectedColor: 'border-indigo-500 bg-gradient-to-br from-indigo-500/20 to-violet-500/20',
  },
  {
    id: 'other',
    title: 'Other',
    icon: '🎯',
    color: 'hover:border-slate-400/50',
    selectedColor: 'border-slate-500 bg-gradient-to-br from-slate-500/20 to-gray-500/20',
  },
];

export const IndustrySelector: React.FC<IndustrySelectorProps> = ({ selectedIndustry, onSelect }) => {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
      {industryOptions.map((industry, index) => {
        const isSelected = selectedIndustry === industry.id;

        return (
          <motion.button
            key={industry.id}
            onClick={() => onSelect(industry.id)}
            className={`
              relative p-4 md:p-6 rounded-xl border-2 text-center
              transition-all duration-300
              ${isSelected
                ? industry.selectedColor
                : `border-slate-200 dark:border-aurora-violet-500/20 bg-white/80 dark:bg-dark-elevated/50 ${industry.color}`
              }
              focus:outline-none focus-visible:ring-2 focus-visible:ring-aurora-violet-500/50
            `}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05, duration: 0.3 }}
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
          >
            {/* Selection indicator */}
            {isSelected && (
              <motion.div
                className="absolute top-2 right-2"
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
              >
                <div className="w-5 h-5 rounded-full bg-gradient-to-r from-emerald-400 to-emerald-500 flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </motion.div>
            )}

            {/* Icon */}
            <div className="text-3xl md:text-4xl mb-2">{industry.icon}</div>

            {/* Title */}
            <h3 className="text-sm md:text-base font-semibold text-slate-900 dark:text-white">
              {industry.title}
            </h3>
          </motion.button>
        );
      })}
    </div>
  );
};

export default IndustrySelector;
